import { useCallback, useEffect, useState } from 'react';

// Cooldown persistido em localStorage. Mantém um botão desabilitado por
// X minutos depois de uma ação (envio de email, etc), sobrevivendo a
// refresh. Atualiza o contador 1×/s só enquanto está ativo; quando
// expira, o intervalo é limpo automaticamente.
//
// Uso:
//   const cd = useCooldown('reset_senha_cooldown', 10);
//   <button disabled={cd.isActive} onClick={async () => {
//       await api.post(...);
//       cd.start();              // dispara 10min
//   }}>
//       {cd.isActive ? `Aguarde ${formatarCooldown(cd.secondsLeft)}` : 'Enviar'}
//   </button>
//
// Pra sincronizar com timestamp vindo do backend (ex: 429 com try_at):
//   cd.startAt(res.try_at);
export default function useCooldown(storageKey, defaultMinutes = 10) {
    const [until, setUntil] = useState(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            const ms = raw ? new Date(raw).getTime() : 0;
            return Number.isFinite(ms) && ms > Date.now() ? ms : 0;
        } catch {
            return 0;
        }
    });

    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!until || until <= Date.now()) return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [until]);

    const secondsLeft = Math.max(0, Math.ceil((until - now) / 1000));
    const isActive = secondsLeft > 0;

    const persistir = (ms) => {
        try { localStorage.setItem(storageKey, new Date(ms).toISOString()); } catch { /* ignore */ }
        setUntil(ms);
        setNow(Date.now());
    };

    const start = useCallback((minutes = defaultMinutes) => {
        persistir(Date.now() + minutes * 60 * 1000);
    }, [defaultMinutes]); // eslint-disable-line react-hooks/exhaustive-deps

    const startAt = useCallback((iso) => {
        const ms = new Date(iso).getTime();
        if (!Number.isFinite(ms) || ms <= Date.now()) return;
        persistir(ms);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return { isActive, secondsLeft, start, startAt };
}

// "9m 59s" / "59s" / "1m" — compacto pra rótulo de botão.
export function formatarCooldown(segundos) {
    if (!segundos || segundos < 0) return '0s';
    if (segundos < 60) return `${segundos}s`;
    const min = Math.floor(segundos / 60);
    const sec = segundos % 60;
    return sec ? `${min}m ${sec}s` : `${min}m`;
}
